export const RECOVERY_PASSWORD_JWT_STORAGE_KEY =
  "mm_user_mgmt_recovery_password_jwt_v1";

export const RECOVERY_EMAIL_CHANGE_JWT_STORAGE_KEY =
  "mm_user_mgmt_recovery_email_change_jwt_v1";

export const PASSWORD_RESET_SENT_STORAGE_KEY =
  "mm_user_mgmt_password_reset_sent_v1";

export type PasswordResetSentStorage = {
  email: string;
  response_type: "email" | "recoveryEmail";
};

export function readRecoveryTokenFromUrlHash(): string {
  if (typeof window === "undefined") return "";
  try {
    const raw = window.location.hash.replace(/^#/, "");
    if (!raw) return "";
    return new URLSearchParams(raw).get("token")?.trim() ?? "";
  } catch {
    return "";
  }
}

export function stripRecoveryJwtFromUrlHash(): void {
  if (typeof window === "undefined") return;
  try {
    const raw = window.location.hash.replace(/^#/, "");
    if (!raw) return;
    const params = new URLSearchParams(raw);
    if (!params.get("token")?.trim()) return;
    params.delete("token");
    const remainder = params.toString();
    const hash = remainder ? `#${remainder}` : "";
    const url = `${window.location.pathname}${window.location.search}${hash}`;
    window.history.replaceState(null, "", url);
  } catch {
    /* noop */
  }
}

export function hydrateRecoveryJwtFromUrlOrStorage(storageKey: string): string {
  if (typeof window === "undefined") return "";
  try {
    const fromUrl = readRecoveryTokenFromUrlHash();
    if (fromUrl) {
      sessionStorage.setItem(storageKey, fromUrl);
      return fromUrl;
    }
    return sessionStorage.getItem(storageKey)?.trim() ?? "";
  } catch {
    return "";
  }
}

export function clearRecoveryJwtStorage(storageKey: string): void {
  try {
    sessionStorage.removeItem(storageKey);
  } catch {
    /* noop */
  }
}

export function writePasswordResetSentContext(
  data: PasswordResetSentStorage,
): void {
  try {
    sessionStorage.setItem(
      PASSWORD_RESET_SENT_STORAGE_KEY,
      JSON.stringify(data),
    );
  } catch {
    /* noop */
  }
}

export function readPasswordResetSentContext(): PasswordResetSentStorage | null {
  try {
    const raw = sessionStorage.getItem(PASSWORD_RESET_SENT_STORAGE_KEY);
    if (!raw?.trim()) return null;
    const parsed = JSON.parse(raw) as {
      email?: unknown;
      response_type?: unknown;
    };
    const email =
      typeof parsed.email === "string" ? parsed.email.trim() : "";
    const response_type =
      parsed.response_type === "email" || parsed.response_type === "recoveryEmail"
        ? parsed.response_type
        : null;
    if (!email || !response_type) return null;
    return { email, response_type };
  } catch {
    return null;
  }
}

export function clearPasswordResetSentContext(): void {
  try {
    sessionStorage.removeItem(PASSWORD_RESET_SENT_STORAGE_KEY);
  } catch {
    /* noop */
  }
}

export function hydratePasswordResetSentFromSessionOrLegacy(): PasswordResetSentStorage | null {
  const fromSession = readPasswordResetSentContext();
  if (fromSession) return fromSession;
  if (typeof window === "undefined") return null;
  try {
    const params = new URLSearchParams(window.location.search);
    const email = (params.get("email") ?? "").trim();
    const rt = params.get("response_type");
    if (
      email.length === 0 ||
      (rt !== "email" && rt !== "recoveryEmail")
    ) {
      return null;
    }
    const payload: PasswordResetSentStorage = { email, response_type: rt };
    writePasswordResetSentContext(payload);
    return payload;
  } catch {
    return null;
  }
}
